import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { NotificationResponse } from '@/api/data-contracts';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';
import { tryParseJson } from '@/shared/lib/json';

import { useMarkNotificationAsRead } from '../../api/useMarkNotificationAsRead';
import { parseNotificationExtraDetails } from '../../model/notificationExtraDetails';
import {
  notificationBodyContext,
  weTransferNotificationValues,
} from '../../model/weTransferReason';
import getMonitorCategoryLabel from './getMonitorCategoryLabel';
import getNotificationLink, { getNotificationNavigationState } from './getNotificationLink';

interface Options {
  pendingOrdersCount?: number;
  onNavigate?: () => void;
}

export interface NotificationContent {
  title: string;
  description: string;
  link: string | undefined;
  markAsRead: () => void;
  open: () => void;
}

const useNotificationContent = (
  notification: NotificationResponse,
  { pendingOrdersCount = 0, onNavigate }: Options = {},
): NotificationContent => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userType = useContext(UserTypeContext);

  const detailsObj = useMemo(
    () => tryParseJson(notification.audit.details ?? '{}'),
    [notification.audit.details],
  );

  const extraDetailsObj = useMemo(
    () => parseNotificationExtraDetails(notification.audit.extraDetails),
    [notification.audit.extraDetails],
  );

  const link = useMemo(
    () =>
      getNotificationLink({
        type: notification.audit.type,
        details: notification.audit.details,
        extraDetails: notification.audit.extraDetails,
        userType,
      }),
    [
      notification.audit.type,
      notification.audit.details,
      notification.audit.extraDetails,
      userType,
    ],
  );

  const notificationType = notification.audit.type;
  const derivedValues: Record<string, unknown> = {};

  if (
    [
      'USER_IMPERSONATED',
      'USER_CREATED',
      'USER_UPDATED',
      'USER_AVATAR_UPDATED',
      'USER_DISABLED',
      'USER_ENABLED',
    ].includes(notificationType)
  ) {
    derivedValues.userName = `${detailsObj?.firstName ?? ''} ${detailsObj?.lastName ?? ''}`.trim();
  } else if (notificationType === 'ACCESS_REQUESTED') {
    derivedValues.userName = notification.audit.userName;
  }

  if (['CLIENT_CREATED', 'CLIENT_UPDATED', 'CLIENT_DELETED'].includes(notificationType)) {
    derivedValues.clientName = detailsObj?.companyName;
  }

  if (
    notificationType.startsWith('LEASING_CLIENT_') ||
    notificationType.startsWith('KIOSK_LEASING_')
  ) {
    const leasingClient = detailsObj?.leasingClient as Record<string, unknown> | undefined;
    derivedValues.leasingClientName = leasingClient?.name ?? '-';
  }

  if (['SERVICE_CREATED', 'SERVICE_UPDATED', 'SERVICE_DELETED'].includes(notificationType)) {
    derivedValues.serviceName = detailsObj?.materialName ?? '-';
  }

  if (notificationType.startsWith('SPACE_')) {
    derivedValues.spaceName = `${detailsObj?.supportName || ''} - ${
      (detailsObj?.locationEntity as Record<string, unknown>)?.locationName || ''
    }`;
  }

  if (notificationType.startsWith('ADVERTISING_PACKAGE_')) {
    derivedValues.advertisingPackageName = detailsObj?.packageName ?? '-';
  }

  const monitorCategoryLabel = getMonitorCategoryLabel(notificationType, extraDetailsObj);
  if (monitorCategoryLabel !== undefined) {
    derivedValues.category = monitorCategoryLabel;
  }

  if (
    notificationType === 'CAMPAIGN_MONITOR_PHOTOS_UPDATED' ||
    notificationType === 'CAMPAIGN_MONITOR_PHOTOS_DELETED'
  ) {
    const rawOrderNumber = extraDetailsObj.orderNumber;
    derivedValues.orderNumber =
      typeof rawOrderNumber === 'string' && rawOrderNumber ? rawOrderNumber : '-';
  }

  const translationValues = {
    ...detailsObj,
    ...extraDetailsObj,
    pendingOrdersCount,
    ...derivedValues,
    ...weTransferNotificationValues(notificationType, detailsObj, t),
  };

  const title = t(`notifications.title.${notification.audit.type}`, translationValues);

  const rawDescription = t(`notifications.body.${notification.audit.type}`, {
    ...translationValues,
    defaultValue: '',
    context: notificationBodyContext(detailsObj, extraDetailsObj),
  });
  const description = rawDescription.replace(/<[^>]*>/g, '').trim();

  const { mutate: markAsRead } = useMarkNotificationAsRead(notification);

  const open = () => {
    if (notification.status === 'UNREAD') markAsRead();
    if (link) {
      queryClient.invalidateQueries({ refetchType: 'active' });
      const separator = link.includes('?') ? '&' : '?';
      const navigationState = getNotificationNavigationState(notification.audit.type);
      navigate(`${link}${separator}_t=${Date.now()}`, { state: navigationState });
      onNavigate?.();
    }
  };

  return { title, description, link, markAsRead, open };
};

export default useNotificationContent;
